import React, { FC, useMemo } from "react";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { t, Trans } from "@helpers/translate";
import { useGraphQL } from "@helpers/graphql";
import {
  Category,
  City,
  Country,
  Language,
  LocationsWithDataQuery,
  PageInfo,
  SupplierFiltersResponse,
  SupplierOrderBy,
  SuppliersQuery,
  TourFiltersResponse,
  ToursQuery,
  TourOrderBy,
} from "@graphql/types";
import { sanitizeSearchUrl, useSearch } from "@helpers/context/SearchContext";
import { useIsClient } from "@helpers/hooks/useIsClient";
import {
  buildMicroFormatData,
  MicroFormatData,
} from "@helpers/getMicroFormatData";
import { buildCanonical, checkNoIndexNoFollow } from "@helpers/getCanonical";
import { useRouter } from "next/router";
import { searchURL } from "@helpers/searchURL";
import Head from "@components/common/Head";
import ProductMicroFormat from "@components/common/MicroFormat/ProductMicroFormat";
import { Container } from "@components/ui/Container";
import { Header } from "@components/pages/SearchPage/Header";
import { Alert } from "@components/ui/Alert";
import { Paragraph } from "@components/ui/Paragraph";
import { SortSelect } from "@components/ui/SortSelect";
import ToursBlock from "@components/common/ToursBlock";
import SuppliersBlock from "@components/common/SupplierBlock";
import { Filters } from "@components/pages/SearchPage/Filters";
import { getNodes } from "@helpers/getNodes";
import { Divider } from "@components/ui/Divider";
import { Breadcrumb } from "@components/ui/Breadcrumb";
import BreadcrumbMicroFormat from "@components/common/MicroFormat/BreadcrumbMicroFormat";
import getFiltersFromURLSearchPage from "@helpers/getFiltersSearchPage";
import { SearchLinksSection } from "@components/pages/SearchPage/SearchLinksSection";
import LocationBlock from "@components/common/LocationBlock/LocationBlock";
import { CategoryTypes } from "@components/common/Category";
import { SearchTabs } from "@components/pages/SearchPage/SearchTabs";
import Pagination from "@components/common/Pagination";
import { COOKIE_TOKEN } from "@constants";
import ScrollToTop from "@components/ui/ScrollToTop/ScrollToTop";
import NotFoundSearch from "@components/pages/NotFoundPage/NotFoundSearch";

type SupplierFiltersResponseCustom = {
  t: "guides";
} & SupplierFiltersResponse;

type TourFiltersResponseCustom = {
  t: "tours";
} & TourFiltersResponse;

type SearchPageProps = {
  canonicalUrl: string;
  microFormatData: MicroFormatData;
  location: string;
  cityData?: City;
  countryData?: Country;
  language: Language | null;
  category: Category | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  isEmpty: boolean;
  pageInfo: PageInfo;
  defaultFiltersAvailable:
    | SupplierFiltersResponseCustom
    | TourFiltersResponseCustom;
  locations?: LocationsWithDataQuery;
  tabsAvailable: CategoryTypes[];
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { query, req } = context;

  const { orderby, ...restQuery } = query;

  // If orderby exists and is in lowercase, redirect with uppercase
  if (orderby && orderby === orderby.toString().toLowerCase()) {
    const newQuery = new URLSearchParams({
      ...restQuery,
      orderBy: (orderby as string).toUpperCase(),
    });

    return {
      redirect: {
        destination: `/s?${newQuery.toString()}`,
        permanent: false,
      },
    };
  }

  const token = req.cookies[COOKIE_TOKEN];
  const graphql = useGraphQL(token, {
    "gwg-ip-country": String(req.headers["x-vercel-ip-country"]) || "",
    "gwg-session-id": String(req.cookies["_ga"]) || "", // google analytics cookie
  });

  // /s?xx=yy&&zz=ww...
  const sanitizationData = await sanitizeSearchUrl(req.url || "");

  if (sanitizationData.needsRedirection) {
    return {
      redirect: {
        destination: sanitizationData.urlSanitized,
        permanent: false,
      },
    };
  }

  const variables = {
    page: query.page ? Number(query.page?.toString()) : 1,
    sizePerPage: 24,
    before: query.before ? String(query.before.toString()) : null,
    after: query.after ? String(query.after.toString()) : null,
  };

  const { SUPPLIER_FILTER, TOURS_FILTER } = getFiltersFromURLSearchPage(query);

  let pageInfo = null as PageInfo | null;
  let data = null as SuppliersQuery | ToursQuery | null;
  let isEmpty = true;

  if (query.t === "guides") {
    const res = await graphql.suppliers({
      ...variables,
      filters: SUPPLIER_FILTER,
    });

    data = res;
    pageInfo = res.suppliers.pageInfo;
    isEmpty = res.suppliers.edges.length <= 0;
  } else {
    const res = await graphql.tours({
      ...variables,
      filters: TOURS_FILTER,
    });

    data = res;
    pageInfo = res.tours.pageInfo;
    isEmpty = res.tours.edges.length <= 0;
  }

  let location = "";

  let countryBySlugQuery;
  if (query.country) {
    countryBySlugQuery = await graphql.countryBySlug({
      slug: query.country as string,
    });

    location = countryBySlugQuery.countryBySlug?.name || "";
  }

  let cityBySlugQuery;
  if (query.city) {
    cityBySlugQuery = await graphql.cityBySlug({
      slug: query.city as string,
    });
    location = cityBySlugQuery?.cityBySlug?.name || "";
  }

  const getDefaultFiltersAvailable = async () => {
    if (query.t === "guides") {
      const data = await graphql.getSuppliersFiltersAvailable({
        filters: {
          ...SUPPLIER_FILTER,
        },
      });

      return { t: query.t, ...data.getSuppliersFiltersAvailable };
    }

    const data = await graphql.getToursFiltersAvailable({
      filters: {
        ...TOURS_FILTER,
      },
    });

    return { t: query.t, ...data.getToursFiltersAvailable };
  };

  return {
    props: {
      canonicalUrl: await buildCanonical(query),
      microFormatData: await buildMicroFormatData(
        query,
        countryBySlugQuery,
        cityBySlugQuery,
        TOURS_FILTER.PRODUCT_TYPES?.[0] ?? undefined
      ),
      location,
      countryData: countryBySlugQuery?.countryBySlug || null,
      cityData: cityBySlugQuery?.cityBySlug || null,
      pageInfo: pageInfo,
      data: data,
      isEmpty: isEmpty,
      defaultFiltersAvailable: await getDefaultFiltersAvailable(),
      language: query?.language
        ? await graphql
            .languages({
              first: 1,
              filters: {
                LANGUAGES_CODES: [query.language.toString()],
              },
            })
            .then((results) => results.languages.edges[0]?.node)
        : null,
      category: query?.category
        ? await graphql
            .categoryBySlug({ slug: query.category.toString() })
            .then((result) => result.categoryBySlug)
        : null,
      locations: sanitizationData.matchLocations,
      tabsAvailable: sanitizationData.tabsAvaialble,
    },
  };
};

const SearchPage: FC<SearchPageProps> = ({
  canonicalUrl,
  microFormatData,
  location,
  countryData,
  cityData,
  language,
  category,
  data,
  locations,
  pageInfo,
  tabsAvailable,
  defaultFiltersAvailable,
  isEmpty,
}) => {
  const { query } = useRouter();

  const { filters, currentTabSelectedTitle, updateTabsWithContent } =
    useSearch();

  updateTabsWithContent(tabsAvailable);

  const isClient = useIsClient();
  const router = useRouter();

  const getProductType = () => {
    let productType = "";
    if (query.t === "tours") {
      productType = "Private Tours";
    } else if (query.t === "guides") {
      productType = "Tour Guides";
    } else if (query.t === "transportation") {
      productType = "Private Car Tours";
    } else if (query.t === "online-tours") {
      productType = "Virtual Tours";
    }
    return productType;
  };

  const numberOfFiltersSelected = useMemo(() => {
    let numberOfFiltersSelected = 0;

    if (query.category) {
      numberOfFiltersSelected++;
    }

    if (query.duration) {
      numberOfFiltersSelected++;
    }
    if (query.language) {
      numberOfFiltersSelected++;
    }
    if (query.adults) {
      numberOfFiltersSelected++;
    }
    if (query.children) {
      numberOfFiltersSelected++;
    }
    if (query.infants) {
      numberOfFiltersSelected++;
    }
    return numberOfFiltersSelected;
  }, [filters]);

  const { title, description } = useMemo(() => {
    let titleBase = `${location} ${getProductType()}`;

    let description = "";

    if (
      numberOfFiltersSelected == 0 ||
      numberOfFiltersSelected >= 2 ||
      query.language === "EN"
    ) {
      return {
        title: `${titleBase} ${
          query.page == undefined || query.page === "1"
            ? ""
            : `- page ${query.page}`
        }`,
        description: `Top ${location} ${getProductType()}: See reviews and photos of the ${location} ${getProductType()}${
          query.page ? ` - page ${query.page}` : ``
        }`,
      };
    }

    const duration = query.duration as string | undefined;

    const categoryFormatted = category?.name || "";
    const languageFormatted = language?.name || "";

    const durationBase = duration?.replaceAll("-", " ").toLowerCase() || "";
    const durationFormatted =
      durationBase.charAt(0).toUpperCase() + durationBase.slice(1);

    if (query.t === "guides") {
      if (query.language) {
        titleBase = `${languageFormatted} ${titleBase}`;
        description = `Top ${languageFormatted} Speaking ${location} ${getProductType()}: See reviews and photos of the ${location} ${getProductType()} Who Speak ${languageFormatted}`;
      }
    } else if (query.t === "tours") {
      if (query.language) {
        titleBase = `${languageFormatted} ${titleBase}`;
        description = `Top ${languageFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${getProductType()} in ${languageFormatted}`;
      } else if (query.category) {
        titleBase = `${categoryFormatted} ${titleBase}`;
        description = `Top ${categoryFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${categoryFormatted} ${getProductType()}`;
      } else if (query.duration) {
        titleBase = `${durationFormatted} ${titleBase}`;
        description = `Top ${durationFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${durationFormatted} ${getProductType()}`;
      }
    } else if (query.t === "transportation") {
      if (query.language) {
        titleBase = `${languageFormatted} ${titleBase}`;
        description = `Top ${languageFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${getProductType()} in ${languageFormatted}`;
      } else if (query.category) {
        titleBase = `${categoryFormatted} ${titleBase}`;
        description = `Top ${categoryFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${categoryFormatted} ${getProductType()}`;
      } else if (query.duration) {
        titleBase = `${durationFormatted} ${titleBase}`;
        description = `Top ${durationFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${durationFormatted} ${getProductType()}`;
      }
    } else if (query.t === "online-tours") {
      if (query.language) {
        titleBase = `${languageFormatted} ${titleBase}`;
        description = `Top ${languageFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${getProductType()} in ${languageFormatted}`;
      } else if (query.category) {
        titleBase = `${categoryFormatted} ${titleBase}`;
        description = `Top ${categoryFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${categoryFormatted} ${getProductType()}`;
      } else if (query.duration) {
        titleBase = `${durationFormatted} ${titleBase}`;
        description = `Top ${durationFormatted} ${location} ${getProductType()}: See reviews and photos of the ${location} ${durationFormatted} ${getProductType()}`;
      }
    }

    return {
      title: `${titleBase} ${query.page ? `- page ${query.page}` : ""}`,
      description: description,
    };
  }, [filters]);

  const getBreadCrumbs = () => {
    // For sub categories show parent category bread-crumb

    if (countryData) {
      return [
        {
          text: t({ id: "breadcrumb.top", message: "Home" }),
          href: "/",
        },
        {
          text: countryData?.name || "",
          href: countryData?.url || "",
        },
        {
          text: t({ id: "breadcrumb.search", message: "Search" }),
          href: router.asPath,
        },
      ];
    }

    if (cityData) {
      return [
        {
          text: t({ id: "breadcrumb.top", message: "Home" }),
          href: "/",
        },
        {
          text: cityData?.country?.name || "",
          href: cityData?.country?.url || "",
        },
        {
          text: cityData?.name || "",
          href: cityData?.url || "",
        },
        {
          text: t({ id: "breadcrumb.search", message: "Search" }),
          href: router.asPath,
        },
      ];
    }

    return [
      {
        text: t({ id: "breadcrumb.top", message: "Home" }),
        href: "/",
      },
      {
        text: t({ id: "breadcrumb.search", message: "Search" }),
        href: router.asPath,
      },
    ];
  };

  const orderByList = useMemo(() => {
    if (filters.t === "guides") {
      return [
        {
          key: SupplierOrderBy.Recommended,
          value: "Recommended",
        },
        {
          key: SupplierOrderBy.TopReviewed,
          value: "Top Reviewed",
        },
      ];
    }

    return [
      {
        key: TourOrderBy.Recommended,
        value: "Recommended",
      },
      {
        key: TourOrderBy.BestSellers,
        value: "Best Sellers",
      },
    ];
  }, [filters]);

  const currentOrderBy = useMemo(() => {
    if (filters.t === "guides") {
      return filters.orderBy || SupplierOrderBy.Recommended;
    }

    return filters.orderBy || TourOrderBy.Recommended;
  }, [filters]);

  return (
    <>
      <Head
        title={title}
        keepQueryParams={true}
        canonical={canonicalUrl}
        description={description}
        noindex={
          checkNoIndexNoFollow(canonicalUrl) || numberOfFiltersSelected >= 2
        }
        nofollow={
          checkNoIndexNoFollow(canonicalUrl) || numberOfFiltersSelected >= 2
        }
      />

      {microFormatData.name !== "" && (
        <ProductMicroFormat
          productID={microFormatData.productId}
          name={microFormatData.name}
          description={microFormatData.description}
          reviewCount={microFormatData.reviewsCount}
        />
      )}
      <div className="max-w-6xl mx-auto">
        <SearchTabs className="sticky top-0 z-10 px-4 md:px-8 bg-white shadow-lg-simple md:shadow-none py-4" />

        <Container className="py-0 pb-8">
          <Divider className="my-0 hidden md:flex" />
          {isClient && (
            <>
              <div className="mt-8">
                <div className="mb-4 w-full">
                  <div className="flex overflow-x-auto w-full items-center">
                    <Breadcrumb items={getBreadCrumbs()} noWrap />
                  </div>
                  <BreadcrumbMicroFormat items={getBreadCrumbs()} />
                </div>

                {locations &&
                  locations?.locations &&
                  locations?.locations?.length > 0 && (
                    <LocationBlock
                      title={t({
                        id: "search.title.destinations",
                        message: "Destinations",
                      })}
                      data={locations}
                    />
                  )}

                <Header
                  title={title}
                  countryName={countryData?.name}
                  cityName={cityData?.name}
                />
              </div>

              {(countryData?.slug || cityData?.country?.slug) === "japan" && (
                <Alert
                  variant="warning"
                  centered
                  showIcon={false}
                  className="p-4"
                >
                  <Paragraph size="sm">
                    <Trans id="search.date.filter.message">
                      Due to high demand, we recommend that you use our date
                      filter to make sure you view tours/guides that are
                      available on your preferred dates.
                    </Trans>
                  </Paragraph>
                </Alert>
              )}
              <div className="flex flex-col justify-between md:flex-row">
                <Filters defaultFiltersAvailable={defaultFiltersAvailable} />
                <div className="flex items-center justify-center">
                  <SortSelect
                    className="border-0"
                    value={currentOrderBy} //key
                    items={orderByList}
                    onChange={(item) => {
                      router.push(searchURL({ ...filters, orderBy: item.key }));
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="mt-4">
            {data && (
              <>
                {defaultFiltersAvailable.t === "guides" ? (
                  <SuppliersBlock
                    data={getNodes(data.suppliers)}
                    className="mb-10"
                  >
                    {pageInfo && (
                      <Pagination
                        pageInfo={pageInfo}
                        offsetPagination={true}
                        showPageOne={false}
                        showIndexBasedPagination={true}
                      />
                    )}
                  </SuppliersBlock>
                ) : (
                  <ToursBlock data={getNodes(data.tours)}>
                    {pageInfo && (
                      <Pagination
                        pageInfo={pageInfo}
                        offsetPagination={true}
                        showPageOne={false}
                        showIndexBasedPagination={true}
                      />
                    )}
                  </ToursBlock>
                )}
              </>
            )}
            {isEmpty && (
              <div className="md:mb-6">
                <NotFoundSearch
                  query={filters.q}
                  currentTabSelectedTitle={currentTabSelectedTitle}
                />
              </div>
            )}
            <SearchLinksSection
              location={location}
              productType={getProductType()}
              filters={defaultFiltersAvailable}
              city={cityData}
              country={countryData}
            />
          </div>

          <ScrollToTop />
        </Container>
      </div>
    </>
  );
};

const Page: FC<SearchPageProps> = ({
  canonicalUrl,
  microFormatData,
  location,
  countryData,
  cityData,
  language,
  category,
  data,
  isEmpty,
  pageInfo,
  tabsAvailable,
  defaultFiltersAvailable,
  locations,
}) => {
  return (
    <>
      <Head
        title={t({
          id: "search.seo.title",
          message: `Search`,
        })}
      />

      <SearchPage
        canonicalUrl={canonicalUrl}
        microFormatData={microFormatData}
        location={location}
        countryData={countryData}
        cityData={cityData}
        language={language}
        category={category}
        data={data}
        isEmpty={isEmpty}
        pageInfo={pageInfo}
        tabsAvailable={tabsAvailable}
        defaultFiltersAvailable={defaultFiltersAvailable}
        locations={locations}
      />
    </>
  );
};

export default Page;
